import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";

export type ThemeMode = "light" | "dark" | "system";

/** Search page sub-header (Top, Trending, Communities, People) */
export type SearchResultTab =
  | "posts"
  | "hashtags"
  | "people"
  | "communities";

/**
 * UI Store - manages global UI state
 */
export const useUiStore = defineStore("ui", () => {
  // Theme state
  const themeMode = ref<ThemeMode>("dark");
  const systemPrefersDark = ref(
    window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  // Sidebar state - default to closed on mobile
  const sidebarOpen = ref(window.innerWidth >= 768);
  const sidebarRail = ref(false);

  // Modal states
  const createPostModalOpen = ref(false);
  const settingsModalOpen = ref(false);

  // Loading states
  const globalLoading = ref(false);
  const loadingMessage = ref("");

  /** Brief “Updating app…” when recovering from stale chunk / deploy skew */
  const chunkRecoveryToastVisible = ref(false);

  // Mobile detection
  const isMobile = ref(window.innerWidth < 768);

  // Feed view mode: 'traditional' | 'single-post'
  // Default to 'single-post' as requested
  const feedViewMode = ref<"traditional" | "single-post">("single-post");
  
  // Feed view mode configuration from app settings
  // 'single-only' | 'traditional-only' | 'both'
  const feedViewModeConfig = ref<"single-only" | "traditional-only" | "both">("both");
  
  // Default feed view mode from app settings (when both are available)
  const defaultFeedViewMode = ref<"traditional" | "single-post">("single-post");

  // Groups view: callback to open create dialog (set by GroupsView when mounted)
  const groupsOpenCreateDialog = ref<(() => void) | null>(null);

  /** Single-community /groups feed: group most visible in vertical scroller — default target for composer */
  const groupsListFocusedGroup = ref<{
    name: string;
    avatar?: string;
  } | null>(null);

  function setGroupsListFocusedGroup(
    payload: { name: string; avatar?: string } | null,
  ): void {
    groupsListFocusedGroup.value = payload;
  }

  // Group feed view: current group shown in header (set by GroupFeedView when mounted, cleared on unmount)
  const currentGroupFeed = ref<{
    name: string;
    avatar?: string;
    /** Cover / banner for header chrome (e.g. group detail background) */
    background?: string;
    ulid?: string;
    isFavorite?: boolean;
    canModerate?: boolean;
  } | null>(null);
  // Callback for favorite button in header (set by GroupFeedView, cleared on unmount)
  const currentGroupFeedToggleFavorite = ref<(() => Promise<void>) | null>(null);
  // Loading state for favorite toggle (set by GroupFeedView while toggling)
  const currentGroupFeedTogglingFavorite = ref(false);
  // When false, header shows minimal "Group" bar; when true, shows avatar + name (set by GroupFeedView based on scroll/slide)
  const currentGroupFeedShowCompact = ref(true);

  /** Profile single-post reel: false on first slide (profile card), true from first post onward — mirrors group feed */
  const currentProfileFeedShowCompact = ref(false);

  /**
   * Quote PiP (single-post feed): user tucked the preview off-screen; reused for every quote post.
   * Persisted so the next quote slide starts tucked until they pull the tab again.
   */
  const quotePipGloballyTucked = ref(false);
  try {
    quotePipGloballyTucked.value =
      localStorage.getItem("quote-pip-tucked") === "1";
  } catch {
    /* private mode */
  }

  function setQuotePipGloballyTucked(tucked: boolean): void {
    quotePipGloballyTucked.value = tucked;
    try {
      localStorage.setItem("quote-pip-tucked", tucked ? "1" : "0");
    } catch {
      /* ignore */
    }
  }

  // Search page: sub-header tabs (Top, Trending, Communities, People)
  const searchResultType = ref<SearchResultTab>("posts");

  /**
   * True after the user commits a search (Enter / quick-search hashtag / “Search for…”).
   * Keeps category tabs + taller header off while typing so autocomplete isn’t crowded.
   */
  const searchShowCategoryTabs = ref(false);

  /** Set by PostDetailView for AppHeader title; cleared on leave */
  const detailChromeTitle = ref<string | null>(null);

  function setDetailChromeTitle(title: string | null): void {
    detailChromeTitle.value = title;
  }

  function setSearchResultType(value: SearchResultTab): void {
    searchResultType.value = value;
  }

  function setSearchShowCategoryTabs(value: boolean): void {
    searchShowCategoryTabs.value = value;
  }

  // Getters
  const isDarkTheme = computed(() => {
    if (themeMode.value === "system") {
      return systemPrefersDark.value;
    }
    return themeMode.value === "dark";
  });

  const effectiveTheme = computed(() => (isDarkTheme.value ? "dark" : "light"));

  // Actions
  function setTheme(mode: ThemeMode): void {
    themeMode.value = mode;
    localStorage.setItem("theme-mode", mode);
  }

  function toggleTheme(): void {
    if (themeMode.value === "dark") {
      setTheme("light");
    } else if (themeMode.value === "light") {
      setTheme("dark");
    } else {
      // System mode - toggle based on current effective theme
      setTheme(isDarkTheme.value ? "light" : "dark");
    }
  }

  function initializeTheme(): void {
    // Load saved theme preference
    const savedTheme = localStorage.getItem("theme-mode") as ThemeMode | null;
    if (savedTheme && ["light", "dark", "system"].includes(savedTheme)) {
      themeMode.value = savedTheme;
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    mediaQuery.addEventListener("change", (e) => {
      systemPrefersDark.value = e.matches;
    });

    // Listen for window resize
    window.addEventListener("resize", () => {
      const wasMobile = isMobile.value;
      isMobile.value = window.innerWidth < 768;

      // Auto-close sidebar when transitioning to mobile
      if (!wasMobile && isMobile.value && sidebarOpen.value) {
        sidebarOpen.value = false;
      }
    });
  }

  function toggleSidebar(): void {
    sidebarOpen.value = !sidebarOpen.value;
  }

  function setSidebarOpen(open: boolean): void {
    sidebarOpen.value = open;
  }

  function setSidebarRail(rail: boolean): void {
    sidebarRail.value = rail;
  }

  function setCurrentGroupFeedTogglingFavorite(value: boolean): void {
    currentGroupFeedTogglingFavorite.value = value;
  }

  function setCurrentGroupFeedToggleFavorite(
    fn: (() => Promise<void>) | null,
  ): void {
    currentGroupFeedToggleFavorite.value = fn;
  }

  function setCurrentGroupFeedShowCompact(show: boolean): void {
    currentGroupFeedShowCompact.value = show;
  }

  function setCurrentProfileFeedShowCompact(show: boolean): void {
    currentProfileFeedShowCompact.value = show;
  }

  function openCreatePostModal(): void {
    createPostModalOpen.value = true;
  }

  function closeCreatePostModal(): void {
    createPostModalOpen.value = false;
  }

  function openSettingsModal(): void {
    settingsModalOpen.value = true;
  }

  function closeSettingsModal(): void {
    settingsModalOpen.value = false;
  }

  function setGlobalLoading(loading: boolean, message: string = ""): void {
    globalLoading.value = loading;
    loadingMessage.value = message;
  }

  function setChunkRecoveryToastVisible(visible: boolean): void {
    chunkRecoveryToastVisible.value = visible;
  }

  function setFeedViewMode(mode: "traditional" | "single-post"): void {
    // Don't allow changing if locked to one mode
    if (feedViewModeConfig.value === "single-only" && mode !== "single-post") {
      return;
    }
    if (feedViewModeConfig.value === "traditional-only" && mode !== "traditional") {
      return;
    }
    
    feedViewMode.value = mode;
    localStorage.setItem("feed-view-mode", mode);
  }
  
  const canToggleFeedMode = computed(() => {
    return feedViewModeConfig.value === "both";
  });

  function initializeFeedViewMode(): void {
    // First, check if settings have been loaded from API
    // If config is 'single-only' or 'traditional-only', force that mode
    if (feedViewModeConfig.value === "single-only") {
      feedViewMode.value = "single-post";
      return;
    }
    if (feedViewModeConfig.value === "traditional-only") {
      feedViewMode.value = "traditional";
      return;
    }
    
    // If both modes are available, check saved preference or use default from settings
    const savedMode = localStorage.getItem("feed-view-mode") as
      | "traditional"
      | "single-post"
      | null;
    if (savedMode && ["traditional", "single-post"].includes(savedMode)) {
      feedViewMode.value = savedMode;
    } else {
      // Use default from app settings (defaults to 'single-post')
      feedViewMode.value = defaultFeedViewMode.value;
    }
  }
  
  function setFeedViewModeConfig(
    config: "single-only" | "traditional-only" | "both",
    defaultMode?: "traditional" | "single-post"
  ): void {
    feedViewModeConfig.value = config;
    if (defaultMode) {
      defaultFeedViewMode.value = defaultMode;
    }
    // Re-initialize to apply the config
    initializeFeedViewMode();
  }

  // Persist sidebar state
  watch(sidebarOpen, (open) => {
    localStorage.setItem("sidebar-open", String(open));
  });

  // Initialize sidebar from storage
  // On mobile (< 768px), always start closed per UI standards
  // On desktop, restore from localStorage
  function initializeSidebar(): void {
    if (isMobile.value) {
      // Mobile/PWA: always hidden by default
      sidebarOpen.value = false;
    } else {
      // Desktop: restore from localStorage if available
      const savedState = localStorage.getItem("sidebar-open");
      if (savedState !== null) {
        sidebarOpen.value = savedState === "true";
      }
    }
  }

  return {
    // State
    themeMode,
    systemPrefersDark,
    sidebarOpen,
    sidebarRail,
    createPostModalOpen,
    settingsModalOpen,
    globalLoading,
    loadingMessage,
    chunkRecoveryToastVisible,
    isMobile,
    feedViewMode,
    feedViewModeConfig,
    defaultFeedViewMode,
    groupsOpenCreateDialog,
    groupsListFocusedGroup,
    currentGroupFeed,
    currentGroupFeedToggleFavorite,
    currentGroupFeedTogglingFavorite,
    currentGroupFeedShowCompact,
    currentProfileFeedShowCompact,
    quotePipGloballyTucked,
    searchResultType,
    searchShowCategoryTabs,
    detailChromeTitle,

    // Getters
    isDarkTheme,
    effectiveTheme,
    canToggleFeedMode,

    // Actions
    setTheme,
    toggleTheme,
    initializeTheme,
    toggleSidebar,
    setSidebarOpen,
    setSidebarRail,
    setCurrentGroupFeedTogglingFavorite,
    setCurrentGroupFeedToggleFavorite,
    setCurrentGroupFeedShowCompact,
    setCurrentProfileFeedShowCompact,
    setQuotePipGloballyTucked,
    setSearchResultType,
    setSearchShowCategoryTabs,
    setDetailChromeTitle,
    setGroupsListFocusedGroup,
    openCreatePostModal,
    closeCreatePostModal,
    openSettingsModal,
    closeSettingsModal,
    setGlobalLoading,
    setChunkRecoveryToastVisible,
    initializeSidebar,
    setFeedViewMode,
    initializeFeedViewMode,
    setFeedViewModeConfig,
  };
});

export type UiStore = ReturnType<typeof useUiStore>;
